import { useState, type ReactNode } from 'react'
import { Helmet } from 'react-helmet-async'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import { Alert } from '../../components/atoms/Alert'
import { Spinner } from '../../components/atoms/Spinner'
import { useAuth } from '../../../context/AuthContext'
import { useDashboard, type DashboardMetrics } from '../../../viewmodels/useDashboard'

/**
 * `/dashboard` — operator landing surface.
 *
 * Chrome (topbar / sidebar / sign-out) lives in DashboardShell — this
 * page renders content ONLY. Read-only widgets fed by GET /dashboard.
 *
 * Split into three tabs (2026-05-29):
 *   - Overview   — KPIs + catalog-state donuts (Products by category,
 *                  Product status, Pricing health).
 *   - Customers  — Customer-status donut + four sign-up time-range
 *                  charts (This week / This month / This quarter / This
 *                  year) in a 2×2 grid.
 *   - Purchases  — Purchase spend by month + Spend by supplier.
 *
 * The tab strip is a rounded-full segmented control with a framer-motion
 * `layoutId` pill that slides between tabs. Tab-panel swaps fade through
 * `AnimatePresence mode="wait"`. Honours reduced-motion (no slide / fade).
 */

const PALETTE = ['#4F6F52', '#BC7E5E', '#A6926B', '#7A9E7E', '#C9A24B', '#8C6A4F', '#6B8E9E', '#B0654F']
const GREEN = '#4F6F52'
const TERRA = '#BC5B4A'
const MUTED = '#9A8C7A'
const BAMBOO = '#A6926B'
const GRID = '#E8DCC8'
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

/** recharts tooltip value type (value can be absent). */
type RVal = number | string | ReadonlyArray<number | string> | undefined

type TabKey = 'overview' | 'customers' | 'carts' | 'purchases'

const TABS: ReadonlyArray<{ readonly key: TabKey; readonly label: string }> = [
  { key: 'overview', label: 'Overview' },
  { key: 'customers', label: 'Customers' },
  { key: 'carts', label: 'Carts' },
  { key: 'purchases', label: 'Purchases' }
]

export function DashboardPage(): JSX.Element {
  const { user, isLoading } = useAuth()
  const { metrics, isLoading: metricsLoading, error } = useDashboard()
  const [tab, setTab] = useState<TabKey>('overview')

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner size="lg" />
      </div>
    )
  }
  if (user === null) {
    return <p className="font-body text-text-secondary">Not signed in.</p>
  }

  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email

  return (
    <div className="space-y-8">
      <Helmet>
        <title>Dashboard — Uyyamadilu</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <header className="space-y-2">
        <h1 className="font-heading text-h1 text-charcoal">
          Welcome, {user.firstName ?? user.email}.
        </h1>
        <p className="font-body text-body text-text-secondary">
          Signed in as <span className="text-charcoal">{fullName}</span>.
        </p>
      </header>

      <TabStrip active={tab} onChange={setTab} />

      {metricsLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner size="lg" />
        </div>
      ) : error !== null || metrics === null ? (
        <Alert tone="error">{error ?? 'Could not load dashboard metrics.'}</Alert>
      ) : (
        <AnimatePresence mode="wait">
          <TabPanel key={tab} tabKey={tab}>
            {tab === 'overview' ? <OverviewTab m={metrics} /> : null}
            {tab === 'customers' ? <CustomersTab m={metrics} /> : null}
            {tab === 'carts' ? <CartsTab m={metrics} /> : null}
            {tab === 'purchases' ? <PurchasesTab m={metrics} /> : null}
          </TabPanel>
        </AnimatePresence>
      )}
    </div>
  )
}

/* ───────────────── tab strip ───────────────── */

function TabStrip({ active, onChange }: { readonly active: TabKey; readonly onChange: (k: TabKey) => void }): JSX.Element {
  const reduce = useReducedMotion() === true
  return (
    <div
      role="tablist"
      aria-label="Dashboard sections"
      className="inline-flex items-center gap-1 rounded-full border border-border bg-bg-primary p-1 shadow-sm"
    >
      {TABS.map((t) => {
        const isActive = t.key === active
        return (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(t.key)}
            className="relative isolate rounded-full px-5 py-2 font-body text-body-sm transition-colors duration-150"
          >
            {isActive ? (
              <motion.span
                layoutId="dashboardTabPill"
                aria-hidden
                className="absolute inset-0 -z-10 rounded-full bg-forest shadow-sm"
                transition={reduce ? { duration: 0 } : { type: 'spring', stiffness: 360, damping: 30 }}
              />
            ) : null}
            <span className={isActive ? 'text-text-light font-medium' : 'text-text-secondary hover:text-charcoal'}>
              {t.label}
            </span>
          </button>
        )
      })}
    </div>
  )
}

function TabPanel({ tabKey, children }: { readonly tabKey: TabKey; readonly children: ReactNode }): JSX.Element {
  const reduce = useReducedMotion() === true
  return (
    <motion.section
      role="tabpanel"
      aria-labelledby={`tab-${tabKey}`}
      initial={reduce ? false : { opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={reduce ? { opacity: 1 } : { opacity: 0, y: -6 }}
      transition={{ duration: 0.18, ease: 'easeOut' }}
    >
      {children}
    </motion.section>
  )
}

/* ───────────────── Overview ───────────────── */

function OverviewTab({ m }: { readonly m: DashboardMetrics }): JSX.Element {
  return (
    <div className="space-y-6">
      <section className="grid grid-cols-2 gap-4">
        <Kpi label="Active products" value={m.activeProductCount} accent={GREEN} />
        <Kpi
          label="Pending cost changes"
          value={m.pendingCostCount}
          accent={m.pendingCostCount > 0 ? TERRA : GREEN}
        />
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        <Donut
          index={0}
          title="Products by category"
          data={m.productsByCategory.map((c, i) => ({
            name: c.label,
            value: c.value,
            color: PALETTE[i % PALETTE.length] ?? GREEN
          }))}
        />
        <Donut
          index={1}
          title="Product status"
          data={[
            { name: 'Active', value: m.productStatus.active, color: GREEN },
            { name: 'Decommissioned', value: m.productStatus.decommissioned, color: MUTED }
          ]}
        />
        <Donut
          index={2}
          title="Pricing health"
          data={[
            { name: 'Healthy', value: m.pricingHealth.healthy, color: GREEN },
            { name: 'Bleeding', value: m.pricingHealth.bleeding, color: TERRA }
          ]}
        />
      </section>
    </div>
  )
}

/* ───────────────── Customers ───────────────── */

function CustomersTab({ m }: { readonly m: DashboardMetrics }): JSX.Element {
  const reduce = useReducedMotion() === true
  const draw = {
    isAnimationActive: !reduce,
    animationBegin: reduce ? 0 : 250,
    animationDuration: reduce ? 0 : 700,
    animationEasing: 'ease-out' as const
  }
  const week = m.customerSignupsThisWeek.map((b) => ({
    name: WEEKDAYS[b.weekday - 1] ?? '',
    value: b.value
  }))
  const monthToDate = m.customerSignupsThisMonth.map((b) => ({
    name: String(b.day),
    value: b.value
  }))
  const quarter = m.customerSignupsThisQuarter.map((b) => ({
    name: MONTHS[b.month - 1] ?? '',
    value: b.value
  }))
  const year = m.customerSignupsByMonth.map((b) => ({
    name: MONTHS[b.month - 1] ?? '',
    value: b.value
  }))

  // Responsive: 1 column on portrait phones (<640px), 2 columns on
  // landscape phones / tablets / desktop (≥640px). Tailwind's `sm`
  // breakpoint at 640px sits below every common phone-landscape width
  // (iPhone SE 667px, iPhone 14 Pro Max 932px) and above every common
  // phone-portrait width (≤430px), which gives the orientation split
  // Kiran asked for without an explicit orientation media query.
  return (
    <div className="space-y-6">
      <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SignupChart index={0} title="Sign-ups · this week" data={week} draw={draw} reduce={reduce} glintId="weekGlint" fillId="weekFill" tickHint="day" />
        <SignupChart index={1} title="Sign-ups · this month" data={monthToDate} draw={draw} reduce={reduce} glintId="monthGlint" fillId="monthFill" tickHint="day-of-month" />
        <SignupChart index={2} title={`Sign-ups · this quarter (${m.year})`} data={quarter} draw={draw} reduce={reduce} glintId="quarterGlint" fillId="quarterFill" tickHint="month" />
        <SignupChart index={3} title={`Sign-ups · this year (${m.year})`} data={year} draw={draw} reduce={reduce} glintId="yearGlint" fillId="yearFill" tickHint="month" />
      </section>
    </div>
  )
}

interface SignupSeries { readonly name: string; readonly value: number }
type DrawProps = {
  readonly isAnimationActive: boolean
  readonly animationBegin: number
  readonly animationDuration: number
  readonly animationEasing: 'ease-out'
}

function SignupChart({
  index,
  title,
  data,
  draw,
  reduce,
  glintId,
  fillId,
  tickHint
}: {
  readonly index: number
  readonly title: string
  readonly data: SignupSeries[]
  readonly draw: DrawProps
  readonly reduce: boolean
  readonly glintId: string
  readonly fillId: string
  readonly tickHint: 'day' | 'day-of-month' | 'month'
}): JSX.Element {
  // For a long month-to-date series, thin x-axis ticks so 1..31 stays legible.
  const xInterval = tickHint === 'day-of-month' ? (data.length > 14 ? 2 : 0) : 0
  return (
    <Card index={index} title={title}>
      <SeriesEmpty empty={data.every((d) => d.value === 0)}>
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={GREEN} stopOpacity={0.35} />
                <stop offset="100%" stopColor={GREEN} stopOpacity={0.02} />
              </linearGradient>
              {reduce ? null : (
                <linearGradient id={glintId} gradientUnits="userSpaceOnUse" x1="-320" y1="0" x2="-160" y2="0">
                  <stop offset="0" stopColor="#FFFFFF" stopOpacity="0" />
                  <stop offset="0.5" stopColor="#FFFFFF" stopOpacity="0.9" />
                  <stop offset="1" stopColor="#FFFFFF" stopOpacity="0" />
                  <animate attributeName="x1" values="-320;1600" dur="2.6s" begin="0.8s" repeatCount="indefinite" />
                  <animate attributeName="x2" values="-160;1760" dur="2.6s" begin="0.8s" repeatCount="indefinite" />
                </linearGradient>
              )}
            </defs>
            <CartesianGrid stroke={GRID} vertical={false} />
            <XAxis dataKey="name" interval={xInterval} tick={{ fontSize: 11, fill: '#7A6E5F' }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#7A6E5F' }} width={32} />
            <Tooltip formatter={(v: RVal) => [Number(v), 'Sign-ups']} />
            <Area type="monotone" dataKey="value" stroke={GREEN} strokeWidth={2} fill={`url(#${fillId})`} {...draw} />
            {reduce ? null : (
              <Line
                type="monotone"
                dataKey="value"
                stroke={`url(#${glintId})`}
                strokeWidth={3}
                dot={false}
                activeDot={false}
                legendType="none"
                tooltipType="none"
                isAnimationActive={false}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </SeriesEmpty>
    </Card>
  )
}

/* ───────────────── Carts ───────────────── */

/**
 * Carts tab — abandonment-focused analytics.
 *
 * Layout (top → bottom):
 *   1. Threshold caption ("Abandoned = ≥ N days") so the reader knows
 *      the operative rule before reading any number.
 *   2. Three KPI tiles: abandoned count, ₹ stranded, abandonment rate.
 *   3. Two donuts side-by-side: status mix + abandoned by owner.
 *   4. Two-line time series: created vs abandoned, day-by-day, this
 *      month-to-date. Gap between the lines = the leak.
 *   5. Horizontal funnel: Created → With items → Stale → Abandoned.
 *      Each step is the count from the status mix; bars shrink left to
 *      right so the drop-off is visible at a glance.
 *
 * All numbers come from a single GET /dashboard response (no extra
 * fetch); the threshold is the live CART.ABANDONEDTHRESHOLDDAYS
 * preference resolved server-side.
 */
function CartsTab({ m }: { readonly m: DashboardMetrics }): JSX.Element {
  const reduce = useReducedMotion() === true
  const draw = {
    isAnimationActive: !reduce,
    animationBegin: reduce ? 0 : 250,
    animationDuration: reduce ? 0 : 700,
    animationEasing: 'ease-out' as const
  }
  const ca = m.cartAnalytics
  const mix = ca.cartStatusMix
  const withItems = mix.active + mix.stale + mix.abandoned
  const totalCarts = mix.empty + withItems
  const rate = withItems === 0 ? 0 : Math.round((mix.abandoned / withItems) * 100)
  const stranded = ca.strandedPaisa / 100

  // Trend overlay: align both series on day-of-month so recharts draws
  // them on the same x-axis. The server sends 1..today; pad to the
  // longer of the two (they should match in practice).
  const trendLen = Math.max(ca.createdThisMonth.length, ca.abandonedThisMonth.length)
  const trend = Array.from({ length: trendLen }, (_, i) => ({
    name: String(i + 1),
    created: ca.createdThisMonth[i]?.value ?? 0,
    abandoned: ca.abandonedThisMonth[i]?.value ?? 0
  }))
  const trendEmpty = trend.every((d) => d.created === 0 && d.abandoned === 0)
  const xInterval = trend.length > 14 ? 2 : 0

  const funnel = [
    { name: 'Created',    value: totalCarts, color: GREEN },
    { name: 'With items', value: withItems,  color: BAMBOO },
    { name: 'Stale',      value: mix.stale,  color: '#C9A24B' },
    { name: 'Abandoned',  value: mix.abandoned, color: TERRA }
  ]
  const funnelEmpty = funnel.every((d) => d.value === 0)

  return (
    <div className="space-y-6">
      <p className="font-body text-body-sm text-text-secondary">
        Abandoned = cart with items + no update in{' '}
        <span className="font-medium text-charcoal">{ca.abandonedThresholdDays}+ days</span>.
        Threshold is editable in{' '}
        <span className="font-medium text-charcoal">Preferences → Cart</span>.
      </p>

      {/* KPIs */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Kpi label="Abandoned carts" value={mix.abandoned} accent={mix.abandoned > 0 ? TERRA : GREEN} />
        <KpiAmount label="₹ stranded" amount={stranded} accent={stranded > 0 ? TERRA : GREEN} />
        <KpiPercent label="Abandonment rate" value={rate} accent={rate > 0 ? TERRA : GREEN} />
      </section>

      {/* Donuts */}
      <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Donut
          index={0}
          title="Cart status mix"
          data={[
            { name: 'Active',     value: mix.active,    color: GREEN },
            { name: 'Stale',      value: mix.stale,     color: '#C9A24B' },
            { name: 'Abandoned',  value: mix.abandoned, color: TERRA },
            { name: 'Empty',      value: mix.empty,     color: MUTED }
          ]}
        />
        <Donut
          index={1}
          title="Abandoned by owner"
          data={[
            { name: 'Guest',    value: ca.abandonedByOwner.guest,    color: BAMBOO },
            { name: 'Customer', value: ca.abandonedByOwner.customer, color: GREEN }
          ]}
        />
      </section>

      {/* Created vs abandoned, this month */}
      <section>
        <Card index={2} title="Created vs abandoned · this month">
          <SeriesEmpty empty={trendEmpty}>
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={trend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="createdFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={GREEN} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={GREEN} stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="abandonedFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={TERRA} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={TERRA} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={GRID} vertical={false} />
                <XAxis dataKey="name" interval={xInterval} tick={{ fontSize: 11, fill: '#7A6E5F' }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#7A6E5F' }} width={32} />
                <Tooltip
                  formatter={(v: RVal, key) => [
                    Number(v), key === 'created' ? 'Created' : 'Abandoned'
                  ]}
                />
                <Area type="monotone" dataKey="created" stroke={GREEN} strokeWidth={2} fill="url(#createdFill)" {...draw} />
                <Area type="monotone" dataKey="abandoned" stroke={TERRA} strokeWidth={2} fill="url(#abandonedFill)" {...draw} />
              </ComposedChart>
            </ResponsiveContainer>
            <Legend items={[
              { label: 'Created', color: GREEN },
              { label: 'Abandoned', color: TERRA }
            ]} />
          </SeriesEmpty>
        </Card>
      </section>

      {/* Funnel */}
      <section>
        <Card index={3} title="Cart funnel">
          <SeriesEmpty empty={funnelEmpty}>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={funnel} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                <CartesianGrid stroke={GRID} horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: '#7A6E5F' }} />
                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: '#7A6E5F' }} />
                <Tooltip formatter={(v: RVal) => [Number(v), 'Carts']} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} {...draw}>
                  {funnel.map((d, i) => (
                    <Cell key={i} fill={d.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </SeriesEmpty>
        </Card>
      </section>
    </div>
  )
}

/* ───────────────── Purchases ───────────────── */

function PurchasesTab({ m }: { readonly m: DashboardMetrics }): JSX.Element {
  const reduce = useReducedMotion() === true
  const spend = m.purchaseSpendByMonth.map((b) => ({ name: MONTHS[b.month - 1] ?? '', value: b.value / 100 }))
  const supplier = m.spendBySupplier.map((s) => ({ name: s.label, value: s.value / 100 }))
  const draw = {
    isAnimationActive: !reduce,
    animationBegin: reduce ? 0 : 250,
    animationDuration: reduce ? 0 : 700,
    animationEasing: 'ease-out' as const
  }
  return (
    <div className="space-y-6">
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card index={0} title={`Purchase spend by month (${m.year})`}>
          <SeriesEmpty empty={spend.every((d) => d.value === 0)}>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={spend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid stroke={GRID} vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#7A6E5F' }} />
                <YAxis tick={{ fontSize: 11, fill: '#7A6E5F' }} width={48}
                  tickFormatter={(v: number) => `₹${v >= 1000 ? `${Math.round(v / 1000)}k` : v}`} />
                <Tooltip formatter={(v: RVal) => [`₹${Number(v).toLocaleString('en-IN')}`, 'Spend']} />
                <Bar dataKey="value" fill={BAMBOO} radius={[4, 4, 0, 0]} {...draw} />
              </BarChart>
            </ResponsiveContainer>
          </SeriesEmpty>
        </Card>

        <Card index={1} title="Spend by supplier">
          <SeriesEmpty empty={supplier.length === 0}>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={supplier} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                <CartesianGrid stroke={GRID} horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#7A6E5F' }}
                  tickFormatter={(v: number) => `₹${v >= 1000 ? `${Math.round(v / 1000)}k` : v}`} />
                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: '#7A6E5F' }} />
                <Tooltip formatter={(v: RVal) => [`₹${Number(v).toLocaleString('en-IN')}`, 'Spend']} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} {...draw}>
                  {supplier.map((_, i) => (
                    <Cell key={i} fill={PALETTE[i % PALETTE.length] ?? GREEN} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </SeriesEmpty>
        </Card>
      </section>
    </div>
  )
}

/* ───────────────── shared atoms ───────────────── */

function Kpi({ label, value, accent }: { readonly label: string; readonly value: number; readonly accent: string }): JSX.Element {
  return (
    <article className="rounded-2xl border border-border bg-bg-primary p-5 shadow-sm">
      <p className="font-body text-body-sm text-text-secondary">{label}</p>
      <p className="mt-2 font-heading text-h1" style={{ color: accent }}>{value}</p>
    </article>
  )
}

/** KPI tile rendered as a rupee amount (en-IN grouping, no decimals). */
function KpiAmount({ label, amount, accent }: { readonly label: string; readonly amount: number; readonly accent: string }): JSX.Element {
  const display = `₹${Math.round(amount).toLocaleString('en-IN')}`
  return (
    <article className="rounded-2xl border border-border bg-bg-primary p-5 shadow-sm">
      <p className="font-body text-body-sm text-text-secondary">{label}</p>
      <p className="mt-2 font-heading text-h1" style={{ color: accent }}>{display}</p>
    </article>
  )
}

/** KPI tile rendered as a percentage with a trailing %. */
function KpiPercent({ label, value, accent }: { readonly label: string; readonly value: number; readonly accent: string }): JSX.Element {
  return (
    <article className="rounded-2xl border border-border bg-bg-primary p-5 shadow-sm">
      <p className="font-body text-body-sm text-text-secondary">{label}</p>
      <p className="mt-2 font-heading text-h1" style={{ color: accent }}>{value}%</p>
    </article>
  )
}

/** Compact legend strip used under multi-series charts (one chip per series). */
function Legend({ items }: { readonly items: ReadonlyArray<{ readonly label: string; readonly color: string }> }): JSX.Element {
  return (
    <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
      {items.map((it) => (
        <li key={it.label} className="flex items-center gap-2 font-body text-body-xs text-text-secondary">
          <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: it.color }} aria-hidden />
          {it.label}
        </li>
      ))}
    </ul>
  )
}

function Card({ title, children, className = '', index = 0 }: {
  readonly title: string
  readonly children: ReactNode
  readonly className?: string
  /** Stagger position within its grid — drives the entrance delay. */
  readonly index?: number
}): JSX.Element {
  const reduce = useReducedMotion() === true
  return (
    <motion.article
      className={`rounded-2xl border border-border bg-bg-primary p-5 shadow-sm ${className}`}
      initial={reduce ? false : { opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: 'spring', stiffness: 320, damping: 17, delay: index * 0.08 }}
    >
      <h2 className="font-heading text-h4 text-charcoal mb-3">{title}</h2>
      {children}
    </motion.article>
  )
}

function SeriesEmpty({ empty, children }: { readonly empty: boolean; readonly children: ReactNode }): JSX.Element {
  if (empty) {
    return (
      <div className="flex h-[220px] items-center justify-center font-body text-body-sm text-text-secondary">
        No data yet.
      </div>
    )
  }
  return <>{children}</>
}

interface Slice { readonly name: string; readonly value: number; readonly color: string }

function Donut({ title, data, index = 0 }: { readonly title: string; readonly data: Slice[]; readonly index?: number }): JSX.Element {
  const total = data.reduce((s, d) => s + d.value, 0)
  return (
    <Card title={title} index={index}>
      {total === 0 ? (
        <div className="flex h-[180px] items-center justify-center font-body text-body-sm text-text-secondary">
          No data yet.
        </div>
      ) : (
        <>
          <div className="animate-spin-slow motion-reduce:animate-none">
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={42}
                  outerRadius={64}
                  paddingAngle={2}
                  isAnimationActive={false}
                >
                  {data.map((d, i) => (
                    <Cell key={i} fill={d.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="mt-2 space-y-1">
            {data.map((d) => (
              <li key={d.name} className="flex items-center justify-between font-body text-body-xs">
                <span className="flex items-center gap-2 text-text-secondary">
                  <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: d.color }} aria-hidden />
                  {d.name}
                </span>
                <span className="font-medium text-charcoal">{d.value}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  )
}
